import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from '../components/formInputs';
import { PreviousEmploymentDetailsFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
};

export const PreviousEmploymentDetailsNew_MX: React.FC<Props> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema, setValidationSchema] =
    useState<ObjectSchema<Partial<PreviousEmploymentDetailsFormModel_MX>>>();

  useEffect(() => {
    setValidationSchema(getValidationSchema());
  }, []);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: PreviousEmploymentDetailsFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(
        t(`employmentDetailsFormWithPreviousForm:previousEmploymentDetailsForm:heading`),
        isDisabledFormSummary
      )}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          initialFormValue={initialValues}
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

export const FormContent = (props) => {
  const { values, submitForm } = useFormikContext<PreviousEmploymentDetailsFormModel_MX>();
  const { disabled, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAllFormControls]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.employmentStatus()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.occupation()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.employer()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.occupationNotes()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.timeWithEmployerFrom(values.timeWithEmployerTo)}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.timeWithEmployerTo()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.netMonthlyIncome()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.netOtherMonthlyIncome()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.searchAddress()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.country()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
    </Form>
  );
};
